import { IContextualMenuItem } from '@fluentui/react';
import { IPIconButton, IPLink, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapExtraSmall, moreIcon } from 'globalStyles';
import { BaseItem, DriveItem } from 'microsoft-graph';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { IDrive, ISite } from '../../services/Graph/SharepointInterfaces';
import { ParentFolderInformation } from './DirectoryExplorer';

interface IDirectoryParents {
  parents: DriveItem[];
  site?: ISite;
  drive?: BaseItem;
  onSelectDriveFolder: (parent: BaseItem, isChild: boolean) => void;
  onSelectDrive: (item: IDrive) => void;
  onBack: () => void;
  onSelectSite: (site?: ISite) => void;
  disableBackBeforeParentInfo?: boolean;
  parentInfo?: ParentFolderInformation;
}

interface IBreadcrumbItem {
  key: string;
  text: string;
  disabled: boolean;
  underline: boolean;
  onClick?: () => void;
}

const maxVisibleBreadcrumbItems = 3;

const DirectoryParents = (props: IDirectoryParents) => {
  const { t } = useTranslation(['sharepoint']);
  const parentsNoRoot = props.parents.filter((p) => !p.root);
  let disableBackIndex = -1;
  let disableRoot = false;

  //When disableBackBeforeLastParent is enabled, the driveItemId in the parentInfo is the last part that can be navigated back to.
  //Look up this id in the parents array to find the index of the last part. All parts before this index will be disabled.
  if (props.disableBackBeforeParentInfo) {
    if (parentsNoRoot.length === 0) {
      disableRoot = true;
    } else {
      disableBackIndex = props.parents.findIndex((p) => p.id === props.parentInfo?.driveItemId);
      if (disableBackIndex >= 0) disableRoot = true;
    }
  }

  const breadcrumbItems: IBreadcrumbItem[] = [];

  if (props.site?.displayName) {
    breadcrumbItems.push({
      key: `site-${props.site.id ?? props.site.displayName}`,
      text: props.site.displayName,
      disabled: (parentsNoRoot.length === 0 && !props.drive) || disableRoot,
      underline: parentsNoRoot.length !== 0 || props.drive !== undefined,
      onClick: () => props.onSelectSite(props.site),
    });
  }

  if (props.drive?.name) {
    breadcrumbItems.push({
      key: `drive-${props.drive.id ?? props.drive.name}`,
      text: props.drive.name,
      disabled: parentsNoRoot.length === 0 || disableRoot,
      underline: parentsNoRoot.length !== 0,
      onClick: () => {
        if (props.drive) props.onSelectDrive(props.drive);
      },
    });
  }

  parentsNoRoot.forEach((parent: BaseItem, index: number) => {
    const disabled = index === parentsNoRoot.length - 1 || index < disableBackIndex;

    breadcrumbItems.push({
      key: `folder-${parent.id ?? index}`,
      text: parent.name ?? '',
      disabled,
      underline: !disabled,
      onClick: () => props.onSelectDriveFolder(parent, false),
    });
  });

  const collapsedBreadcrumbItems =
    breadcrumbItems.length > maxVisibleBreadcrumbItems
      ? breadcrumbItems.slice(0, breadcrumbItems.length - maxVisibleBreadcrumbItems)
      : [];
  const visibleBreadcrumbItems =
    collapsedBreadcrumbItems.length > 0 ? breadcrumbItems.slice(-maxVisibleBreadcrumbItems) : breadcrumbItems;

  const overflowMenuItems: IContextualMenuItem[] = collapsedBreadcrumbItems.map((item) => ({
    key: item.key,
    text: item.text,
    disabled: item.disabled,
    onClick: item.disabled ? undefined : item.onClick,
  }));

  const renderSeparator = (key: string) => (
    <IPText key={key} block nowrap styles={{ root: { flexShrink: 0 } }}>
      {'>'}
    </IPText>
  );

  const renderBreadcrumbItem = (item: IBreadcrumbItem) => (
    <IPText
      block
      nowrap
      key={item.key}
      title={item.text}
      styles={{
        root: {
          maxWidth: 180,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    >
      {item.disabled ? (
        item.text
      ) : (
        <IPLink disabled={item.disabled} underline={item.underline} onClick={item.onClick}>
          {item.text}
        </IPLink>
      )}
    </IPText>
  );

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={globalStackTokensGapExtraSmall}
      verticalAlign="center"
      styles={{ root: { minWidth: 0, maxWidth: '100%', overflow: 'hidden' } }}
    >
      <IPIconButton
        iconProps={{ iconName: 'NavigateBack' }}
        onClick={props.onBack}
        disabled={
          (parentsNoRoot.length === 0 && props.drive === undefined) || props.parents.length - 1 <= disableBackIndex
        }
      />
      {collapsedBreadcrumbItems.length > 0 && (
        <IPIconButton
          iconProps={moreIcon}
          title={t('sharepoint:CommandBar.BreadcrumbOverflow')}
          menuProps={{ items: overflowMenuItems }}
          styles={{ menuIcon: { display: 'none' } }}
        />
      )}
      {collapsedBreadcrumbItems.length > 0 &&
        visibleBreadcrumbItems.length > 0 &&
        renderSeparator('overflow-separator')}
      {visibleBreadcrumbItems.map((item, index) => (
        <Fragment key={item.key}>
          {renderBreadcrumbItem(item)}
          {index < visibleBreadcrumbItems.length - 1 && renderSeparator(`${item.key}-separator`)}
        </Fragment>
      ))}
    </IPStack>
  );
};

export default DirectoryParents;
